import { Injectable, ViewContainerRef } from '@angular/core';
import { SohoModalDialogRef, SohoModalDialogService } from 'ids-enterprise-ng';
import { LMSubFormDialogComponent } from './lm-sub-form-dialog.component';
import { AppConstants } from '@utils/app.constants';
import { Observable } from 'rxjs';
import { LMModel } from '@form/model';
import { LMCommonUtilService, LMUniqueIdService } from '@utils/index';
import { LMBusyIndicatorService, LMFormatUtilService } from '@services/index';
import { LMDialogButtonKey } from '@utils/app.constants';
import { LMFormSpecUtilService } from '@form/services';

export enum LMSubFormDialogResult {
  RESPONSE_CANCEL = 0,
  RESPONSE_OK,
  RESPONSE_ERROR
}

export interface LMSubFormResult {
  result: LMSubFormDialogResult;
  dataView?: LMDataView;
  model?: LMModel;
  bindIds?: Array<string>;
  error?: LMSubFormError;
}

export interface LMSubFormError {
  message?: string;
  details?: string;
  status?: string;
  statusText?: string;
}

export interface LMSubFormDialogOptions {
  placeholder: ViewContainerRef;
  fieldId: string;
  busFormSpec: LMBusFormSpec;
  title: LMMessageRule;
  dataView: LMDataView;
  formUrlInfo: LMFormUrlInfo;
  formSpec: LMFormSpec;
  model?: LMModel;
}

@Injectable()
export class LMSubFormDialogService {
  constructor(
    private sohoModalDialogService: SohoModalDialogService,
    private lmCommonUtilService: LMCommonUtilService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmFormSpecUtilService: LMFormSpecUtilService
  ) {}

  public promptSubFormDialog(args: LMSubFormDialogOptions): Observable<LMSubFormResult> {
    const formSpec: LMFormSpec = this.buildSubFormDlgParams(args);
    const specFields: LMSpecFields = formSpec.busFormSpec.fields;
    const title = this.lmFormatUtilService.formatMessageRule(args.title, args.dataView, specFields);
    return this.openDialog(args.formUrlInfo, formSpec, args.dataView, args.placeholder, args.fieldId, title, args.model);
  }

  private buildSubFormDlgParams(args: LMSubFormDialogOptions): LMFormSpec {
    // make a deep clone of the formSpec
    const formSpec: LMFormSpec = JSON.parse(JSON.stringify(args.formSpec));

    // replace the busFormSpec with a deep clone of the args.busFormSpec
    formSpec.busFormSpec = JSON.parse(JSON.stringify(args.busFormSpec));

    // update with the correct subFormType
    formSpec.busFormSpec.subFormType = AppConstants.BUTTON_TYPE_SUBFORM;

    // keep the original namePrefix, name, and current namePrefix
    formSpec.busFormSpec.namePrefix = this.lmUniqueIdService.uniqueId([args.formSpec.busFormSpec.namePrefix, args.formSpec.busFormSpec.name, formSpec.busFormSpec.namePrefix]);

    return formSpec;
  }

  private openDialog(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    dataView: LMDataView,
    placeholder: ViewContainerRef,
    fieldId: string,
    title: string,
    parentModel?: LMModel
  ): Observable<LMSubFormResult> {
    this.lmBusyIndicatorService.showBusyIndicator();

    return new Observable<LMSubFormResult>(observer => {

      if (!this.lmFormSpecUtilService.isStandardForm(formSpec)) {
        this.lmBusyIndicatorService.hideBusyIndicator();

        const formType = formSpec.busFormSpec.formType;
        const error: LMSubFormError = {
          message: this.lmFormatUtilService.formatMessage(Soho.Locale.translate('InvalidSubFormType'), formType),
          status: AppConstants.VIEW_EXCEPTION
        };
        const result: LMSubFormResult = {
          result: LMSubFormDialogResult.RESPONSE_ERROR,
          error
        };
        observer.next(result);
        observer.complete();
        return;
      }

      let dlgComponent: LMSubFormDialogComponent;
      let dialog: SohoModalDialogRef<LMSubFormDialogComponent>;
      const bindIds = this.lmCommonUtilService.getBindIdsFromFormSpec(formSpec);
      const formNamePrefix = (formSpec.busFormSpec.namePrefix) ? formSpec.busFormSpec.namePrefix : '';
      const formName = formSpec.busFormSpec.name;

      // Build the buttons array
      const buttons = [
        {
          // TODO: The button default disables the button. A bug in controls?
          // tslint:disable-next-line - shadowed variable e
          id: this.lmUniqueIdService.uniqueId([formNamePrefix, formName, LMDialogButtonKey.CANCEL]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            dialog.close(<LMSubFormResult> {
              result: LMSubFormDialogResult.RESPONSE_CANCEL
            });
          }
        },
        {
          id: this.lmUniqueIdService.uniqueId([formNamePrefix, formName, LMDialogButtonKey.OK]),
          text: Soho.Locale.translate(LMDialogButtonKey.OK),
          validate : false,
          isDefault: true,
          click: (e, modal) => {
            dialog.close(<LMSubFormResult> {
              result:   LMSubFormDialogResult.RESPONSE_OK,
              dataView: dlgComponent.formComponent.formModel.getData(),
              model:    dlgComponent.formComponent.formModel,
              bindIds
            });
          }
        }
      ];

      dialog = this.sohoModalDialogService
        .modal(LMSubFormDialogComponent, placeholder, {fullsize: 'responsive'})
        .buttons(buttons)
        .title(title)
        .isAlert(false)
        .beforeOpen(() => {
          this.lmBusyIndicatorService.hideBusyIndicator();
          return true;
        })
        .open();

      dialog.apply((subForm: LMSubFormDialogComponent) => {
        dlgComponent = subForm;

        subForm.formOptions = {};
        subForm.formUrlInfo = formUrlInfo;
        subForm.formSpec = formSpec;
        subForm.dataView = dataView;
        subForm.parentModel = parentModel;
      });

      dialog.afterClose(result => {
        observer.next(result);
        observer.complete();
      });
    });
  }
}
